import { Component, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { EfileAdminService } from '../../adminservices/efile-admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-admin-shell',
    templateUrl: './efile-admin-shell.component.html',
    styleUrls: ['./efile-admin-shell.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileAdminShellComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  activeTab = 0;
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  headerData: any = {};
  rowData: any = {};
  taxYears: any[] = [];

  selectedTaxYear: any = null;
  selectedScenario: any = null;
  consolGroups: any[] = [];
  selectedConsolGroup: any = null;
  taxPayers: any[] = [];
  selectedTaxPayers: any[] = [];
  efiles: any[] = [];

  loadingGroups = false;
  loadingTaxPayers = false;
  loadingSystems = false;
  pushing = false;

  constructor(
    private efileAdminService: EfileAdminService,
    private snackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.taxYears = this.gtwGlobal.global?.inputs?.tax_year?.values || [];
    if (Array.isArray(this.taxYears) && this.taxYears.length > 0) {
      this.selectedTaxYear =
        this.taxYears.find(
          (t: any) => String(t.value) === String(this.rowData?.TAX_YEAR || this.headerData?.globalParams?.tax_year)
        ) || this.taxYears[0];
      this.selectedScenario =
        this.selectedTaxYear?.scenarios?.find((s: any) => s.default_scenario === 'Y') ||
        this.selectedTaxYear?.scenarios?.[0];
      this.loadGroups();
    }
  }

  private get ssoId(): string {
    return this.headerData?.userSettingsData?.user?.sso_id || this.gtwApp.USER_SETTINGS?.user?.sso_id || '';
  }

  onTaxYearChange(): void {
    this.selectedScenario = this.selectedTaxYear?.scenarios?.find((s: any) => s.default_scenario === 'Y') || this.selectedTaxYear?.scenarios?.[0];
    this.loadGroups();
  }

  onScenarioChange(): void {
    this.loadGroups();
  }

  loadGroups(): void {
    if (!this.selectedTaxYear || !this.selectedScenario) return;
    this.loadingGroups = true;
    this.efileAdminService.getFilingGroups(
      this.baseURLs.api,
      String(this.selectedTaxYear.value),
      String(this.selectedScenario.scenario),
      'FEDERAL',
      this.ssoId
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.loadingGroups = false;
        this.consolGroups = res?.jsonObject || [];
        this.selectedConsolGroup = this.consolGroups[0] || null;
        this.loadTaxPayers();
      },
      error: () => {
        this.loadingGroups = false;
        this.consolGroups = [];
      }
    });
  }

  loadTaxPayers(): void {
    if (!this.selectedConsolGroup) return;
    this.loadingTaxPayers = true;
    this.efileAdminService.getTaxPayers(
      this.baseURLs.api,
      String(this.selectedTaxYear.value),
      String(this.selectedScenario.scenario),
      'FEDERAL',
      this.ssoId,
      String(this.selectedConsolGroup?.FILING_GROUP || '')
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.loadingTaxPayers = false;
        this.taxPayers = res?.jsonObject || [];
        this.selectedTaxPayers = [];
        this.efiles = [];
      },
      error: () => {
        this.loadingTaxPayers = false;
        this.taxPayers = [];
      }
    });
  }

  onTaxPayerToggle(tp: any): void {
    const idx = this.selectedTaxPayers.findIndex((x: any) => x.VALUE === tp.VALUE);
    if (idx >= 0) this.selectedTaxPayers.splice(idx, 1);
    else this.selectedTaxPayers.push(tp);
    this.loadSystems();
  }

  isTaxPayerSelected(tp: any): boolean {
    return this.selectedTaxPayers.some((x: any) => x?.VALUE === tp?.VALUE);
  }

  loadSystems(): void {
    const taxPayerCsv = this.selectedTaxPayers.map((x: any) => x.VALUE).join(',');
    this.loadingSystems = true;
    this.efileAdminService.getSystems(
      this.baseURLs.admin,
      String(this.selectedTaxYear?.value || ''),
      String(this.selectedScenario?.scenario || ''),
      taxPayerCsv,
      'FEDERAL'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.loadingSystems = false;
        this.efiles = (res?.data || []).map((x: any) => ({ ...x, selected: false }));
      },
      error: () => {
        this.loadingSystems = false;
        this.efiles = [];
      }
    });
  }

  hasSelectedSystems(): boolean {
    return this.efiles.some((e: any) => e.selected);
  }

  push(): void {
    if (this.pushing || !this.hasSelectedSystems()) return;
    this.pushing = true;
    const taxPayerCsv = this.selectedTaxPayers.map((x: any) => x.VALUE).join(',');
    const systemList = this.efiles.filter((e: any) => e.selected).map((e: any) => e.system_id).join(',');
    this.efileAdminService.pushData(this.baseURLs.admin, {
      filingType: 'FEDERAL',
      filingState: 'FEDERAL',
      tax_year: this.selectedTaxYear?.value || '',
      scenario: this.selectedScenario?.scenario || '',
      filingGroupId: this.selectedConsolGroup?.FILING_GROUP || '',
      groupName: this.selectedConsolGroup?.GROUP_NAME || '',
      tax_payer: taxPayerCsv,
      cdrno: '',
      reportingperiod: '',
      jcd_key: 250,
      systemList,
      generateZip: 0
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.pushing = false;
        if (res?.callSuccess === '1' || res?.data?.callSuccess === '1') {
          this.snackbar.openSnackBar('Data migration for selected systems has been successfully initiated!', 'gtw-snackbar--green');
        } else {
          this.snackbar.openSnackBar(res?.errorMessage || 'Data migration failed to initiate', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.pushing = false;
        this.snackbar.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
      }
    });
  }
}
